/**
 * 真实数据加载器 - 替换mock数据
 * 遵循Linus哲学：简单直接，先让它工作
 */

import { getTeamDisplayNameByDate, sortTeamAliases } from '@/utils/team';
import type {
  Player,
  RankingData,
  Team,
  PlayerEloHistory,
  HistoricalPlayer,
  TeamAlias,
} from '@/types';

// ELO历史原始数据结构
interface EloHistoryRawData {
  meta: {
    startDate: string;
    endDate: string;
    totalRecords: number;
    originalRecords: number;
    consolidated: boolean;
  };
  players: Record<
    string,
    {
      dates: string[];
      elos: number[];
      ranks: (number | null)[];
    }
  >;
}

// 缓存数据，避免重复加载
let playersCache: Player[] | null = null;
let teamsCache: Team[] | null = null;
let rankingsCache: RankingData[] | null = null;
let eloHistoryCache: Record<string, PlayerEloHistory[]> | null = null;
let historicalPlayersCache: HistoricalPlayer[] | null = null;
let eloHistoryRawCache: EloHistoryRawData | null = null; // 原始ELO历史数据缓存
let statsCache: {
  totalPlayers?: number;
  totalTeams?: number;
  averageElo?: number;
  highestElo?: number;
  lowestElo?: number;
  lastUpdated?: string;
} | null = null;
let teamAliasesCache: TeamAlias[] | null = null;

// ELO衰减配置
const ELO_DECAY_CONFIG = {
  INACTIVE_THRESHOLD_DAYS: 120, // 不活跃阈值（天）
  ELO_MINIMUM_THRESHOLD: 1600, // 最低ELO阈值，低于此值将被剔除
  DECAY_RATE_PER_DAY: 1, // 每天衰减分数
} as const;

/**
 * 计算两个日期之间的天数差
 */
function calculateDaysDifference(dateStr1: string, dateStr2: string): number {
  const date1 = new Date(dateStr1);
  const date2 = new Date(dateStr2);
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * 应用ELO衰减逻辑
 */
function applyEloDecay(
  player: Player,
  endDate: string,
  lastMatchDate: string
): Player {
  const inactiveDays = calculateDaysDifference(endDate, lastMatchDate);

  if (inactiveDays <= ELO_DECAY_CONFIG.INACTIVE_THRESHOLD_DAYS) {
    // 不活跃天数未超过阈值，不衰减
    return player;
  }

  // 计算衰减后的ELO
  const decayDays = inactiveDays - ELO_DECAY_CONFIG.INACTIVE_THRESHOLD_DAYS;
  const decayAmount = decayDays * ELO_DECAY_CONFIG.DECAY_RATE_PER_DAY;
  const decayedElo = Math.max(0, player.currentElo - decayAmount);

  return {
    ...player,
    currentElo: decayedElo,
  };
}

/**
 * 加载原始ELO历史数据（用于获取endDate和最后比赛日期）
 */
export async function loadEloHistoryRaw(): Promise<EloHistoryRawData> {
  if (eloHistoryRawCache) return eloHistoryRawCache;

  try {
    const response = await fetch('/data/elo-history.json');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: EloHistoryRawData = await response.json();
    eloHistoryRawCache = data;
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('加载原始ELO历史数据失败:', error);
    return {
      meta: {
        startDate: '',
        endDate: '',
        totalRecords: 0,
        originalRecords: 0,
        consolidated: false,
      },
      players: {},
    };
  }
}

/**
 * 加载战队数据
 */
export async function loadTeams(): Promise<Team[]> {
  if (teamsCache) return teamsCache;

  try {
    const response = await fetch('/data/teams.json');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const rawTeams: Team[] = (await response.json()) || [];

    const aliases = await loadTeamAliases();

    const aliasMap = new Map<number, TeamAlias[]>();
    aliases.forEach(alias => {
      const list = aliasMap.get(alias.teamId);
      if (list) {
        list.push(alias);
      } else {
        aliasMap.set(alias.teamId, [alias]);
      }
    });

    aliasMap.forEach((list, teamId) => {
      aliasMap.set(teamId, sortTeamAliases(list));
    });

    teamsCache = rawTeams.map(team => ({
      ...team,
      aliases: aliasMap.get(team.id) ?? [],
    }));

    return teamsCache;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('加载战队数据失败:', error);
    return [];
  }
}

/**
 * 加载战队别名数据
 */
export async function loadTeamAliases(): Promise<TeamAlias[]> {
  if (teamAliasesCache) return teamAliasesCache;

  try {
    const response = await fetch('/data/team-aliases.json');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: TeamAlias[] = await response.json();
    teamAliasesCache = data || [];
    return teamAliasesCache;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('加载战队别名数据失败:', error);
    teamAliasesCache = [];
    return teamAliasesCache;
  }
}

/**
 * 加载选手数据（不包含团队详情）
 */
export async function loadPlayersRaw(): Promise<Player[]> {
  if (playersCache) return playersCache;

  try {
    const response = await fetch('/data/players.json');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    playersCache = await response.json();
    return playersCache || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('加载选手数据失败:', error);
    return [];
  }
}

/**
 * 加载选手数据（包含关联的团队详情）
 */
export async function loadPlayers(): Promise<Player[]> {
  const [players, teams] = await Promise.all([loadPlayersRaw(), loadTeams()]);

  // 创建战队映射以便快速查找
  const teamsMap = new Map(teams.map(team => [team.id, team]));

  // 为每个选手关联战队信息
  return players.map(player => {
    const { currentTeam: _currentTeam, ...playerWithoutTeam } = player;
    return {
      ...playerWithoutTeam,
      currentTeam: player.currentTeamId
        ? teamsMap.get(player.currentTeamId)
        : undefined,
    };
  });
}

/**
 * 加载排名数据
 */
export async function loadRankings(): Promise<RankingData[]> {
  if (rankingsCache) return rankingsCache;

  try {
    const response = await fetch('/data/rankings.json');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    rankingsCache = await response.json();
    return rankingsCache || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('加载排名数据失败:', error);
    return [];
  }
}

/**
 * 加载统计数据
 */
export async function loadStats(): Promise<{
  totalPlayers?: number;
  totalTeams?: number;
  averageElo?: number;
  highestElo?: number;
  lowestElo?: number;
  lastUpdated?: string;
}> {
  if (statsCache) return statsCache;

  try {
    const response = await fetch('/data/stats.json');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    statsCache = await response.json();
    return statsCache || {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('加载统计数据失败:', error);
    return {};
  }
}

/**
 * 加载战队数据（包含基于前5名选手ELO的平均值和排名）
 */
export async function loadTeamsWithEloRanking(): Promise<Team[]> {
  const [players, teams] = await Promise.all([
    loadPlayersRaw(), // 使用原始数据，避免重复关联
    loadTeams(),
  ]);

  // 为每个战队计算前5名选手的平均ELO
  const teamsWithElo = teams.map(team => {
    // 找到属于该战队的所有选手
    const teamPlayers = players.filter(
      player => player.currentTeamId === team.id
    );

    // 按ELO降序排序，取前5名
    const topPlayers = teamPlayers
      .sort((a, b) => b.currentElo - a.currentElo)
      .slice(0, 5);

    // 计算平均ELO
    const averageElo =
      topPlayers.length > 0
        ? Math.round(
            topPlayers.reduce((sum, player) => sum + player.currentElo, 0) /
              topPlayers.length
          )
        : null;

    return {
      ...team,
      averageElo,
      playerCount: teamPlayers.length,
      // 为UI提供额外信息
      topPlayersCount: topPlayers.length,
    };
  });

  // 按平均ELO降序排序，没有ELO的战队排在最后
  return teamsWithElo.sort((a, b) => {
    if (a.averageElo === null) return 1;
    if (b.averageElo === null) return -1;
    return b.averageElo - a.averageElo;
  });
}

/**
 * 根据ID获取战队信息
 */
export async function getTeamById(teamId: number): Promise<Team | null> {
  const teams = await loadTeams();
  return teams.find(t => t.id === teamId) || null;
}

/**
 * 根据数字ID获取选手信息（用于详情页面）
 */
export async function getPlayerByNumericId(id: number): Promise<Player | null> {
  const players = await loadPlayers();
  return players.find(p => p.id === id) || null;
}

/**
 * 根据战队ID获取战队成员列表
 */
export async function getTeamMembers(teamId: number): Promise<Player[]> {
  const players = await loadPlayers();
  return players.filter(p => p.currentTeamId === teamId);
}

/**
 * 根据playerId字符串获取选手信息
 */
export async function getPlayerById(playerId: string): Promise<Player | null> {
  const players = await loadPlayers();
  return players.find(p => p.playerId === playerId) || null;
}

/**
 * 获取排名前N的选手
 */
export async function getTopPlayers(limit: number = 50): Promise<Player[]> {
  const players = await loadPlayers();
  return players.sort((a, b) => a.currentRank - b.currentRank).slice(0, limit);
}

/**
 * 获取带衰减的排名前N的选手
 */
export async function getTopPlayersWithDecay(
  limit: number = 50
): Promise<Player[]> {
  // 1. 获取原始数据
  const [players, eloHistoryRaw] = await Promise.all([
    loadPlayers(),
    loadEloHistoryRaw(),
  ]);

  // 2. 获取数据结束日期
  const endDate = eloHistoryRaw.meta?.endDate;
  if (!endDate) {
    // 如果没有结束日期，回退到原有逻辑
    return getTopPlayers(limit);
  }

  // 3. 为每个选手应用衰减逻辑
  const playersWithDecay = players.map(player => {
    const playerHistory = eloHistoryRaw.players?.[player.id.toString()];
    if (!playerHistory?.dates?.length) {
      return player; // 没有历史数据，不衰减
    }

    const lastMatchDate = playerHistory.dates[playerHistory.dates.length - 1];
    return applyEloDecay(player, endDate, lastMatchDate);
  });

  // 4. 过滤低于阈值的选手
  const filteredPlayers = playersWithDecay.filter(
    player => player.currentElo >= ELO_DECAY_CONFIG.ELO_MINIMUM_THRESHOLD
  );

  // 5. 重新排序并重新分配排名，然后取Top N
  const sortedPlayers = filteredPlayers.sort(
    (a, b) => b.currentElo - a.currentElo
  );
  return sortedPlayers.slice(0, limit).map((player, index) => ({
    ...player,
    currentRank: index + 1,
  }));
}

/**
 * 加载ELO历史数据(分层存储格式)
 */
export async function loadEloHistory(): Promise<
  Record<string, PlayerEloHistory[]>
> {
  if (eloHistoryCache) return eloHistoryCache;

  try {
    const response = await fetch('/data/elo-history.json');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const layeredData = await response.json();

    // 将分层存储格式转换为前端使用的格式
    const convertedData: Record<string, PlayerEloHistory[]> = {};

    if (layeredData.players) {
      for (const [playerId, playerData] of Object.entries(
        layeredData.players
      )) {
        const { dates, elos, ranks, teamIds } = playerData as {
          dates: string[];
          elos: number[];
          ranks: (number | null)[];
          teamIds?: (number | null)[];
        };
        convertedData[playerId] = dates.map((date: string, index: number) => ({
          id: index + 1,
          playerId: parseInt(playerId),
          teamId: teamIds?.[index] ?? null,
          date,
          eloRating: elos[index],
          mu: 0, // 不再需要
          sigma: 0, // 不再需要
          globalRank: ranks[index] || undefined,
        }));
      }
    }

    eloHistoryCache = convertedData;
    return convertedData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('加载ELO历史数据失败:', error);
    return {};
  }
}

/**
 * 获取特定选手的ELO历史数据
 */
export async function getPlayerEloHistory(
  playerId: number
): Promise<PlayerEloHistory[]> {
  const eloHistory = await loadEloHistory();
  return eloHistory[playerId.toString()] || [];
}

/**
 * 加载历史最高ELO选手排名数据
 * 从elo-history.json计算每个选手的历史最高ELO，结合players.json的选手信息
 */
export async function loadHistoricalPlayers(): Promise<HistoricalPlayer[]> {
  if (historicalPlayersCache) return historicalPlayersCache;

  try {
    const [eloHistory, players, teams] = await Promise.all([
      loadEloHistory(),
      loadPlayersRaw(),
      loadTeams(),
    ]);

    // 创建战队映射
    const teamsMap = new Map(teams.map(team => [team.id, team]));

    // 为每个选手计算历史最高ELO
    const historicalPlayers: HistoricalPlayer[] = [];

    players.forEach(player => {
      const playerEloHistory = eloHistory[player.id.toString()];
      if (!playerEloHistory || playerEloHistory.length === 0) {
        return; // 跳过没有ELO历史的选手
      }

      // 找到历史最高ELO对应的记录
      let peakRecord: PlayerEloHistory | null = null;

      for (const record of playerEloHistory as PlayerEloHistory[]) {
        if (!peakRecord || record.eloRating > peakRecord.eloRating) {
          peakRecord = record;
        }
      }

      if (!peakRecord) return;

      const peakTeam = peakRecord.teamId
        ? teamsMap.get(peakRecord.teamId)
        : undefined;
      const teamAtPeak = peakTeam
        ? getTeamDisplayNameByDate(peakTeam, peakRecord.date)
        : undefined;

      historicalPlayers.push({
        id: player.id,
        playerId: player.playerId,
        realName: player.realName || player.playerId,
        peakElo: Math.round(peakRecord.eloRating),
        peakDate: peakRecord.date,
        currentElo: player.currentElo,
        peakRank: peakRecord.globalRank || 1, // 默认排名1，如果没有记录
        country: player.country,
        teamAtPeak,
        isActive: player.status === 'Active',
      });
    });

    // 按历史最高ELO排序
    historicalPlayers.sort((a, b) => b.peakElo - a.peakElo);

    historicalPlayersCache = historicalPlayers;
    return historicalPlayers;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('加载历史最高ELO数据失败:', error);
    return [];
  }
}

/**
 * 获取Top N历史最高ELO选手
 */
export async function getTopHistoricalPlayers(
  limit: number = 50
): Promise<HistoricalPlayer[]> {
  const historicalPlayers = await loadHistoricalPlayers();
  return historicalPlayers.slice(0, limit);
}
